import _ from 'underscore';
import React, { useMemo, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Label, Stack, Toggle } from '@bingads-webui-react/fabric-control';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { withCustomValidation } from '@bingads-webui-react/validation';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { CampaignSubTypes } from '@bingads-webui-campaign-react/constants';

const Title = ({ titleKey }) => {
  const i18n = useContext(I18nContext);
  const titleText = i18n.getString(titleKey);

  return (
    <Stack verticalAlign="center" horizontal tokens={{ childrenGap: 8 }}>
      <Label className="collapse-title" >
        {titleText}
        <HelpPopup
          topic="pop_BA_PredictiveTargeting_OptimizedDisabled"
          aria-label={i18n.getString(_TL_('Click to learn more about {{item}}'), { item: titleText })}
        />
      </Label>
    </Stack>
  );
};

Title.propTypes = {
  titleKey: PropTypes.string.isRequired,
};

const PredictiveTargetingControlHeaderInternal = ({
  validationProps,
  updateValidation,
  onToggleEnabledChanged,
  alwaysDisabled,
  campaignSubType,
  EnableMSANDisplayCampaigns,
  EnableMSANVideoCampaigns,
}) => {
  const { data } = validationProps;

  let { UseOptimizedTargeting } = data;

  const activity = useContext(ActivityContext);

  const onChange = (e, checked) => {
    updateValidation([], { UseOptimizedTargeting: checked });

    if (activity) {
      activity.signal(`onPredectiveTargetingChanged - isPredTargetingEnabled: ${UseOptimizedTargeting}`);
    }
  };

  const isEnabled = useMemo(() => {
    const doesAdGroupHaveAudienceTargeted = data.audiences && data.audiences.length > 0;
    const doesAdGroupHaveInterestTargeted = data.interests && data.interests.length > 0;
    const doesAdGroupHaveCompanyNameTarget = (data.companyNameTargets && data.companyNameTargets.length > 0) || (data.negativeCompanyNameTargets && data.negativeCompanyNameTargets.length > 0);
    const doesAdGroupHaveJobFunctionTarget = (data.jobTargets && data.jobTargets.length > 0) || (data.negativeJobTargets && data.negativeJobTargets.length > 0);
    const doesAdGroupHaveIndustryTarget = (data.industry && data.industry.length > 0) || (data.negativeIndustry && data.negativeIndustry.length > 0);
    const doesAdGroupHaveAgeTarget = data.ageRanges && data.ageRanges.length < 6;
    const doesAdGroupHaveGenderTarget = data.genderTypes && data.genderTypes.length < 3;
    const doesAdGroupHaveDeviceTarget = data.deviceTypes && data.deviceTypes.length < 3;

    const isVideoCampaign = campaignSubType === CampaignSubTypes.BrandAwarenessVideoAds;

    return doesAdGroupHaveAudienceTargeted || doesAdGroupHaveCompanyNameTarget || doesAdGroupHaveInterestTargeted ||
    doesAdGroupHaveJobFunctionTarget || doesAdGroupHaveIndustryTarget ||
      doesAdGroupHaveAgeTarget || doesAdGroupHaveGenderTarget ||
      (isVideoCampaign && EnableMSANVideoCampaigns && doesAdGroupHaveDeviceTarget);
  }, [data, EnableMSANVideoCampaigns, campaignSubType]);

  // if no targets and use optimized targeting is disabled it, enable it.
  if (!_.isEmpty(data) && !isEnabled && !UseOptimizedTargeting) {
    if (EnableMSANDisplayCampaigns || EnableMSANVideoCampaigns) {
      UseOptimizedTargeting = true;
    }
    updateValidation([], { UseOptimizedTargeting: true });
  }

  useEffect(() => {
    onToggleEnabledChanged(isEnabled);
  }, [isEnabled, onToggleEnabledChanged]);

  return (
    <Stack verticalAlign="center" horizontal tokens={{ childrenGap: 8 }}>
      <Toggle
        inlineLabel={false}
        onChange={onChange}
        checked={UseOptimizedTargeting}
        disabled={alwaysDisabled || !isEnabled}
        styles={{
          root: {
            marginBottom: 0,
          },
        }}
      />
      <Title titleKey={_TL_('Predictive targeting')} />
    </Stack>
  );
};

PredictiveTargetingControlHeaderInternal.propTypes = {
  updateValidation: PropTypes.func.isRequired,
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      UseOptimizedTargeting: PropTypes.bool,
      audiences: PropTypes.shape([]).isRequired,
      interests: PropTypes.shape([]).isRequired,
      companyNameTargets: PropTypes.shape([]).isRequired,
      negativeCompanyNameTargets: PropTypes.shape([]).isRequired,
      jobTargets: PropTypes.shape([]).isRequired,
      negativeJobTargets: PropTypes.shape([]).isRequired,
      industry: PropTypes.shape([]).isRequired,
      negativeIndustry: PropTypes.shape([]).isRequired,
      ageRanges: PropTypes.shape([]).isRequired,
      genderTypes: PropTypes.shape([]).isRequired,
      deviceTypes: PropTypes.shape([]).isRequired,
    }),
  }).isRequired,
  onToggleEnabledChanged: PropTypes.func.isRequired,
  campaignSubType: PropTypes.string.isRequired,
  alwaysDisabled: PropTypes.bool,
  EnableMSANDisplayCampaigns: PropTypes.bool.isRequired,
  EnableMSANVideoCampaigns: PropTypes.bool.isRequired,
};

PredictiveTargetingControlHeaderInternal.defaultProps = {
  alwaysDisabled: false,
};

export const PredictiveTargetingControlHeader = withCustomValidation(PredictiveTargetingControlHeaderInternal);
